import { ReactNode, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { I18nextContext } from 'gatsby-plugin-react-i18next';
import { JsonLd } from 'react-schemaorg';
import { Organization, WebSite } from 'schema-dts';

const Seo = ({ description, meta, title, children }: { description?: string, meta?: any, title: string, children?: ReactNode }) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
          }
        }
      }
    `
  )
  const context = useContext(I18nextContext);

  const metaDescription = description || site.siteMetadata.description
  const defaultTitle = site.siteMetadata?.title

  return (
    <Helmet
      htmlAttributes={{
        language: context.language,
        lang: context.language
      }}
      title={title}
      titleTemplate={defaultTitle ? `%s | ${defaultTitle}` : null}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
      ].concat(meta)}
    >
      {children}
    </Helmet>
  )
}

Seo.defaultProps = {
  meta: [],
  description: ``,
}

export const DefaultHead = ({
  title,
  pathname,
  description,
  children
}: {
  title: string,
  pathname: string,
  description?: string,
  children?: ReactNode
}) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
          }
        }
      }
    `
  )
  const { language } = useContext(I18nextContext);
  const url = `https://watchproject21.com${pathname}`;
  const metaDescription = description || site.siteMetadata.description;
  const metaTitle = `${title} | ${site.siteMetadata.title}`.trim();

  return (
    <>
      <html lang={language} />
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="og:url" content={url} />
      <meta name="og:locale" content={language} />
      <meta name="og:site_name" content="WatchProject21" />
      <meta name="og:title" content={metaTitle} />
      <meta name="og:description" content={metaDescription} />
      <meta name="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:domain" content="watchproject21.com" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <JsonLd<Organization>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'WatchProject21',
          url: 'https://watchproject21.com',
          // logo: 'https://watchproject21.com/logo.png',
          sameAs: [
            'https://www.facebook.com/watchproject21',
            'https://www.instagram.com/watchproject21',
          ],
        }}
      />
      <JsonLd<WebSite>
        item={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          url: 'https://watchproject21.com',
          name: 'WatchProject21',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://watchproject21.com/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      {children}
    </>
  )
}

export default Seo
